// ----------------------------------------------------------------------
// <copyright file="Izenda.LicenseInfo.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var m = require('mobx');
  var extendObservable = require('mobx').extendObservable;
  var localizer = require('share/localization/Izenda.Localizer');
  var CONSTANTS = require('../data/Izenda.LicenseInfoConstants');
  const { isNil, find } = require('lodash/fp');

  function LicenseInfo() {
    this.hasValidLicense = false;

    var props = {
      licenseInfoDetail: {},
      licenseStatus: {},
      // For hidden license token and key on UI
      validatedLicense: false,
      isValidLicense: false,
      disableLicense: true,
      exceedLostConnectionAllowPeriod: false,
      validationError: {},
      licenseInValidMsg: '',
      productVersion: '',
      isConnectedToLicenseServer: true,
      isActiveLicense: function() {
        return this.integrationActiveLicense && this.isValidLicense;
      },
      integrationActiveLicense: function() {
        return (
          this.numberOfDayToExpire > 0 &&
          this.numberOfDayToValid === 0 &&
          !this.disableLicense &&
          !this.exceedLostConnectionAllowPeriod
        );
      },
      startDate: function() {
        return this.licenseInfoDetail.startDate || '';
      },
      endDate: function() {
        return this.licenseInfoDetail.endDate || '';
      },
      tokenKey: function() {
        return this.licenseInfoDetail.tokenKey || '';
      },
      licenseKey: function() {
        return this.licenseInfoDetail.licenseKey || '';
      },
      enableValidateButton: function() {
        return this.licenseOnlineMode ? !!this.licenseKey : !!(this.tokenKey && this.licenseKey);
      },
      licenseOnlineMode: function() {
        return this.licenseInfoDetail.licenseOnlineMode;
      },
      licenseEndDate: function() {
        return this.licenseInfoDetail.licenseEndDate || '';
      },
      previousStartDate: function() {
        return this.licenseInfoDetail.previousStartDate || '';
      },
      previousEndDate: function() {
        return this.licenseInfoDetail.previousEndDate || '';
      },
      modules: function() {
        return this.licenseInfoDetail.modules || [];
      },
      previousModules: function() {
        return this.licenseInfoDetail.previousModules || [];
      },
      hasMapModules: function() {
        return !!find(function(module) {
          return module.name === CONSTANTS.MAP_MODULES_NAME;
        }, this.modules);
      },
      numberOfDayToValid: function() {
        return (
          (this.licenseInfoDetail && this.licenseInfoDetail.numberOfDayToValid) ||
          (this.licenseStatus && this.licenseStatus.numberOfDayToValid)
        );
      },
      meetExprireWarningPeriod: function() {
        return (this.licenseStatus && this.licenseStatus.meetExprireWarningPeriod) || false;
      },
      numberOfDayToExpire: function() {
        return this.licenseStatus && this.licenseStatus.numberOfDayToExpire;
      },
      trialLicense: function() {
        return (this.licenseStatus && this.licenseStatus.trialLicense) || false;
      },
      isOnlineLicenseValidInLostConnectionAllowPeriod: function() {
        return (
          this.licenseOnlineMode &&
          this.isValidLicense &&
          !this.isConnectedToLicenseServer &&
          !this.exceedLostConnectionAllowPeriod
        );
      }
    };

    extendObservable(this, props);
  }

  /**
   * [readLicenseStatus description]
   * @param  {object} status [receive from API]
   */
  LicenseInfo.prototype.readLicenseStatus = m.action(function(status) {
    status = status || {};
    this.licenseStatus = status;
    this.disableLicense = status.disabled;
    this.exceedLostConnectionAllowPeriod = status.exceedLostConnectionAllowPeriod;
    this.isConnectedToLicenseServer = status.connected;
    status.exceedLostConnectionAllowPeriod && (this.isValidLicense = false);
  });

  /**
   * read validate response from api
   * @param  {object} response [from API]
   */
  LicenseInfo.prototype.readValidateLicense = m.action(function(response) {
    this.validatedLicense = true;
    this.isValidLicense = response.success;
    if (response.success) {
      this.licenseInfoDetail = response;
      this.hasValidLicense = true;
    }
  });

  /**
   * read license information response from api
   * @param  {object} response from api
   */
  LicenseInfo.prototype.readLicenseInforation = m.action(function(response) {
    // Set default license mode = online
    if (!response.success) {
      response.licenseOnlineMode = true;
    }
    this.hasValidLicense = this.isValidLicense = this.validatedLicense = isNil(response.success)
      ? !!response.licenseKey
      : response.success;
    this.disableLicense = !response.licenseEnable;
    this.validationError = this.disableLicense ? [CONSTANTS.MESSAGE.MG10] : [];
    this.licenseInValidMsg = '';
    this.licenseInfoDetail = response;
    this.productVersion = response.productVersion
      ? String.format(localizer.getText('COMMON_CRITERIA_VERSION') + ' {0}', response.productVersion)
      : '';
  });

  /**
   * Reset license information
   */
  LicenseInfo.prototype.resetLicenseInfo = m.action(function() {
    this.validatedLicense = false;
    this.isValidLicense = false;
    this.hasValidLicense = false;
    this.exceedLostConnectionAllowPeriod = false;
    this.licenseInfoDetail = {};
    this.licenseStatus = {};
  });

  /**
   * Update license info by key
   * This license info will be set as editable and invalid license
   * @param {type} key
   * @param {type} value
   */
  LicenseInfo.prototype.updateLicenseInfoByKey = m.action(function(key, value) {
    if (this.validatedLicense) {
      this.validatedLicense = false;
    }

    if (this.isValidLicense) {
      this.isValidLicense = false;
    }
    this.licenseInfoDetail[key] = value;
  });

  /**
   * license info construtor for test
   * @type {construtor}
   */
  LicenseInfo.prototype.LicenseInfo = LicenseInfo;

  module.exports = exports = new LicenseInfo();
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.LicenseInfo.js